import React, { FC, useEffect, useMemo } from 'react';

import { Box } from '@mui/system';

import {
  AdminLegendVariant,
  IBookedPharmacistData,
  adminActions,
  isLoadingSelector,
  resetStatus,
  successOrSelector,
} from '@pharmaplan/common';
import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';

import {
  resetDrawer,
  setBack,
  setFooterShow,
  setTitle,
} from '../../../actions/drawerActions';
import DrawerOutOfDateTitle from '../../common/DrawerOutOfDateTitle';
import useOptimizedAvatar from '../../../hooks/useOptimizedAvatar';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import useAdminEvent from '../../../hooks/Admin/useAdminEvent';
import useGetItems from '../../../hooks/useGetItems';
import strings from '../../../localization';

import SingleBooking from './SingleBooking';
import useMultiShiftFilter from '../../../hooks/useMultiShiftFilter';
import { showSuccess } from '../Profile/Pharmacist/PharmacistViewProfile/helper';
import useSelfService from '../../../hooks/useSelfService';
import useAdminDashboard from '../../../hooks/Admin/useAdminDashboard';

const cancelActions = [
  adminActions.adminCancelBooking,
  adminActions.adminCancelMultiple,
];

interface IBookingList {
  date: string;
  eventIds?: Array<string>;
}

const BookingList: FC<IBookingList> = ({ date, eventIds = [] }) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();
  const { dashBasedRequest } = useAdminDashboard();

  const success = useAppSelector((state) =>
    successOrSelector(cancelActions, state));
  const { adminEvents: bookingList } = useMultiShiftFilter();
  const filteredBookingList = useMemo(() => {
    if (eventIds.length === 0) {
      return bookingList;
    }

    return (bookingList as IBookedPharmacistData[]).filter((item) =>
      eventIds.includes(item.bookingId));
  }, [bookingList, eventIds]);

  const { getAdminEventByDate } = useAdminEvent();

  const loading = useAppSelector((state) =>
    isLoadingSelector([adminActions.getAdminEvent], state));

  useEffect(() => {
    const title = (
      <DrawerOutOfDateTitle
        date={date}
        dateLabel={strings.bookedWorkshiftsFormatted}
      />
    );

    dispatch(setTitle({ title }));
    dispatch(setBack(null));

    dispatch(setFooterShow(false));
    getAdminEventByDate({
      selectedDate: date.split('T')[0],
      type: AdminLegendVariant.booked,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pIds = useMemo(
    () =>
      (filteredBookingList as IBookedPharmacistData[]).map(
        (item) =>
item[idStore.id as keyof typeof item] as string,
      ),
    [filteredBookingList],
  );

  useOptimizedAvatar(pIds);
  const { getPharmacistImage } = useGetItems();

  useEffect(() => {
    if (success) {
      dashBasedRequest();
      dispatch(resetStatus(cancelActions));
      dispatch(resetDrawer());
      showSuccess(
        dispatch,
        isSelfService
          ? strings.bookingCancelledSuccessfully
          : strings.submitSuccessfully,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  return (
    <Box>
      {loading ? (
        <SingleBooking
          image=""
          item={filteredBookingList?.[0] as IBookedPharmacistData}
        />
      ) : (
        (filteredBookingList as IBookedPharmacistData[]).map((item) =>
          (
            <SingleBooking
              image={getPharmacistImage(item.pharmacistId)}
              key={item.bookingId}
              item={item}
            />
          ))
      )}
    </Box>
  );
};

export default BookingList;
